import { useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import axios from "axios";
import { toast } from "sonner";
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Minus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ToolbarBtn = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={`editor-btn-${title?.toLowerCase().replace(/\s+/g, "-")}`}
    className={`p-2 transition-colors ${
      active ? "text-[#C5A059] bg-[#C5A059]/10" : "text-white/50 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <span className="w-px h-5 bg-[#262626] mx-1" />;

export default function RichTextEditor({ value, onChange, placeholder = "Skriv något..." }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Image.configure({ HTMLAttributes: { class: "rounded my-4 max-w-full" } }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-[#C5A059] underline hover:opacity-80", target: "_blank", rel: "noopener noreferrer" },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[300px] p-5 focus:outline-none font-['Outfit'] text-white/80",
      },
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("URL", previous || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const token = localStorage.getItem("admin_token");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      editor.chain().focus().setImage({ src: imageUrl }).run();
      toast.success("Bild infogad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      e.target.value = "";
    }
  };

  if (!editor) return null;

  return (
    <div className="bg-[#0A0A0A] border border-[#262626] focus-within:border-[#C5A059]/50 transition-colors">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#262626] bg-[#0F0F0F]">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Rubrik 1"><Heading1 size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Rubrik 2"><Heading2 size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Rubrik 3"><Heading3 size={16} /></ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Fet"><Bold size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Kursiv"><Italic size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Genomstruken"><Strikethrough size={16} /></ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Punktlista"><List size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numrerad lista"><ListOrdered size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citat"><Quote size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Avskiljare"><Minus size={16} /></ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Vänster"><AlignLeft size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centrera"><AlignCenter size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Höger"><AlignRight size={16} /></ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Länk"><LinkIcon size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => fileInputRef.current?.click()} title="Bild"><ImageIcon size={16} /></ToolbarBtn>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
        <div className="flex-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Ångra"><Undo size={16} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Gör om"><Redo size={16} /></ToolbarBtn>
      </div>

      <EditorContent editor={editor} data-testid="rich-text-editor" />
    </div>
  );
}
